import React, { useState, useEffect, useRef, useCallback } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import apiClient from "../../../services/api";
import { ErrorBoundary } from "../../common/ErrorBoundary";
import { AuditLogTimeline, AuditLogEntry, DEFAULT_FIELD_LABELS } from "../../common/audit";

/**
 * Maps API error messages to user-friendly Polish messages.
 * This prevents information disclosure by hiding internal details (UUIDs, constraint names).
 */
const errorMessages: Record<string, string> = {
  // Not found errors
  "konfiguracja nie znaleziona": "Konfiguracja nie zostala znaleziona",
  // Conflict errors
  "konfiguracja o tej nazwie juz istnieje":
    "Konfiguracja o tej nazwie juz istnieje",
  // Validation errors
  "nieprawidlowy format uuid": "Blad walidacji danych",
  "wymagana minimum 1 grupa slow kluczowych":
    "Wymagana minimum 1 grupa slow kluczowych",
  "kazda grupa musi zawierac minimum 1 slowo kluczowe":
    "Kazda grupa musi zawierac minimum 1 slowo kluczowe",
  "nazwa jest wymagana": "Nazwa jest wymagana",
  "nazwa moze miec maksymalnie": "Nazwa moze miec maksymalnie 100 znakow",
  "opis moze miec maksymalnie": "Opis moze miec maksymalnie 2000 znakow",
};

/**
 * Extracts user-friendly error message from API error response.
 * Logs original error for debugging while returning sanitized message to user.
 */
const getErrorMessage = (error: any): string => {
  const rawMessage = error.response?.data?.message || error.message;

  // Normalize message to string (NestJS validation errors return array)
  let originalMessage: string;
  if (Array.isArray(rawMessage)) {
    originalMessage = rawMessage[0] || "";
  } else if (typeof rawMessage === "string") {
    originalMessage = rawMessage;
  } else if (rawMessage && typeof rawMessage === "object") {
    originalMessage = rawMessage.message || JSON.stringify(rawMessage);
  } else {
    originalMessage = "";
  }

  // Log original error for debugging (includes internal details) - dev only
  if (process.env.NODE_ENV !== "production") {
    console.error("[EditProcessingConfigModal] API Error:", {
      status: error.response?.status,
      message: originalMessage,
      data: error.response?.data,
    });
  }

  // Check for known error messages (case-insensitive)
  if (originalMessage) {
    const lowerMessage = originalMessage.toLowerCase();
    for (const [pattern, friendlyMessage] of Object.entries(errorMessages)) {
      if (lowerMessage.includes(pattern)) {
        return friendlyMessage;
      }
    }
  }

  // Generic fallback - never expose raw error message
  if (error.response?.status >= 500) {
    return "Blad serwera. Sprobuj ponownie pozniej.";
  }
  if (error.response?.status === 404) {
    return "Nie znaleziono konfiguracji.";
  }
  if (error.response?.status === 409) {
    return "Operacja nie moze byc wykonana z powodu konfliktu danych.";
  }
  if (error.response?.status === 400) {
    return "Blad walidacji danych.";
  }
  if (!error.response) {
    return "Blad polaczenia. Sprawdz polaczenie z internetem.";
  }

  return "Wystapil nieoczekiwany blad. Sprobuj ponownie.";
};

interface EditProcessingConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  configId?: string | null;
}

interface ContentExtractionConfig {
  extractAttachments: boolean;
  extractImages: boolean;
  imageBlacklist: string[];
  imageWhitelist: string[];
  maxAttachments: number;
  attachmentPriority: string[];
}

interface ClassificationConfig {
  keywordGroups: string[][];
  useInflections: boolean;
}

interface DataExtractionConfig {
  preferHtml: boolean;
  prompts: {
    dateExtraction: string;
    outputSchema: Record<string, unknown>;
  };
}

interface ProcessingConfigFormData {
  name: string;
  description: string;
  isDefault: boolean;
  contentExtraction: ContentExtractionConfig;
  classification: ClassificationConfig;
  dataExtraction: DataExtractionConfig;
}

const defaultFormData: ProcessingConfigFormData = {
  name: "",
  description: "",
  isDefault: false,
  contentExtraction: {
    extractAttachments: true,
    extractImages: false,
    imageBlacklist: [],
    imageWhitelist: [],
    maxAttachments: 3,
    attachmentPriority: [],
  },
  classification: {
    keywordGroups: [],
    useInflections: false,
  },
  dataExtraction: {
    preferHtml: false,
    prompts: {
      dateExtraction: "",
      outputSchema: {},
    },
  },
};

const EditProcessingConfigModal: React.FC<EditProcessingConfigModalProps> = ({
  isOpen,
  onClose,
  configId,
}) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] =
    useState<ProcessingConfigFormData>(defaultFormData);
  const [initialFormData, setInitialFormData] =
    useState<ProcessingConfigFormData>(defaultFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<
    "basic" | "extraction" | "classification" | "data" | "history"
  >("basic");
  const firstInputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  // For keyword groups editing
  const [newKeywordGroup, setNewKeywordGroup] = useState("");

  // Raw text for comma-separated array fields. Kept separate from the parsed
  // array so the user can freely type commas/spaces without them being stripped
  // mid-edit (the array only contains trimmed, non-empty tokens).
  const [arrayFieldRaw, setArrayFieldRaw] = useState<{
    imageBlacklist: string;
    imageWhitelist: string;
    attachmentPriority: string;
  }>({
    imageBlacklist: "",
    imageWhitelist: "",
    attachmentPriority: "",
  });

  /**
   * Check if form has unsaved changes by comparing current form data with initial state.
   */
  const isDirty = useCallback((): boolean => {
    return JSON.stringify(formData) !== JSON.stringify(initialFormData);
  }, [formData, initialFormData]);

  /**
   * Show confirmation dialog if there are unsaved changes.
   * Returns true if user confirms or there are no changes.
   */
  const confirmClose = useCallback((): boolean => {
    if (!isDirty()) return true;
    return window.confirm(
      "Masz niezapisane zmiany. Czy na pewno chcesz zamknac?"
    );
  }, [isDirty]);

  // Fetch config data if editing
  const { data: configData, isLoading: isLoadingConfig } = useQuery({
    queryKey: ["grants-processing-config", configId],
    queryFn: async () => {
      const { data } = await apiClient.get(
        `/grants/admin/processing-configs/${configId}`
      );
      return data;
    },
    enabled: !!configId && isOpen,
  });

  // Fetch audit log data if editing and on history tab
  const {
    data: auditData,
    isLoading: isLoadingAudit,
    error: auditError,
    refetch: refetchAudit,
  } = useQuery<{
    data: AuditLogEntry[];
    pagination: { limit: number; offset: number; total: number; hasMore: boolean };
  }>({
    queryKey: ["grants-processing-config-audit", configId],
    queryFn: async () => {
      const { data } = await apiClient.get(
        `/grants/admin/processing-configs/${configId}/audit`,
        { params: { limit: 20 } }
      );
      return data;
    },
    enabled: !!configId && isOpen && activeTab === "history",
  });

  // Populate form when config data is loaded
  useEffect(() => {
    if (configData) {
      const loadedData: ProcessingConfigFormData = {
        name: configData.name || "",
        description: configData.description || "",
        isDefault: configData.isDefault ?? false,
        contentExtraction: configData.contentExtraction || defaultFormData.contentExtraction,
        classification: configData.classification || defaultFormData.classification,
        dataExtraction: configData.dataExtraction || defaultFormData.dataExtraction,
      };
      setFormData(loadedData);
      setInitialFormData(loadedData);
      setArrayFieldRaw({
        imageBlacklist: loadedData.contentExtraction.imageBlacklist.join(", "),
        imageWhitelist: loadedData.contentExtraction.imageWhitelist.join(", "),
        attachmentPriority:
          loadedData.contentExtraction.attachmentPriority.join(", "),
      });
    }
  }, [configData]);

  // Reset initial form data when modal opens for a new config (create mode)
  useEffect(() => {
    if (isOpen && !configId) {
      setInitialFormData(defaultFormData);
      setArrayFieldRaw({
        imageBlacklist: "",
        imageWhitelist: "",
        attachmentPriority: "",
      });
    }
  }, [isOpen, configId]);

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      await apiClient.post("/grants/admin/processing-configs", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grants-processing-configs"] });
      onClose();
      resetForm();
    },
    onError: (error: any) => {
      handleApiError(error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      await apiClient.put(`/grants/admin/processing-configs/${configId}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grants-processing-configs"] });
      queryClient.invalidateQueries({
        queryKey: ["grants-processing-config", configId],
      });
      onClose();
      resetForm();
    },
    onError: (error: any) => {
      handleApiError(error);
    },
  });

  const handleApiError = (error: any) => {
    // Use sanitized error message - never expose raw backend messages
    setGlobalError(getErrorMessage(error));
  };

  const resetForm = () => {
    setFormData(defaultFormData);
    setInitialFormData(defaultFormData);
    setErrors({});
    setGlobalError(null);
    setActiveTab("basic");
    setNewKeywordGroup("");
  };

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setErrors({});
      setGlobalError(null);

      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        isDefault: formData.isDefault,
        contentExtraction: formData.contentExtraction,
        classification: formData.classification,
        dataExtraction: formData.dataExtraction,
      };

      if (configId) {
        updateMutation.mutate(payload);
      } else {
        createMutation.mutate(payload);
      }
    },
    [formData, configId, updateMutation, createMutation]
  );

  const handleClose = () => {
    if (!createMutation.isPending && !updateMutation.isPending) {
      if (!confirmClose()) return;
      resetForm();
      onClose();
      // Return focus to trigger element after modal closes
      triggerRef.current?.focus();
    }
  };

  // Store trigger element reference when modal opens
  useEffect(() => {
    if (isOpen) {
      triggerRef.current = document.activeElement as HTMLElement;
    }
  }, [isOpen]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (
        e.key === "Escape" &&
        !createMutation.isPending &&
        !updateMutation.isPending
      ) {
        if (!confirmClose()) return;
        resetForm();
        onClose();
        // Return focus to trigger element after ESC close
        triggerRef.current?.focus();
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose, createMutation.isPending, updateMutation.isPending, confirmClose]);

  // Handle Cmd+S (Mac) / Ctrl+S (Windows) keyboard shortcut to save
  useEffect(() => {
    const handleSaveShortcut = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        // Only trigger save if modal is open, no request is pending, and form has no validation errors
        if (!createMutation.isPending && !updateMutation.isPending) {
          handleSubmit({ preventDefault: () => {} } as React.FormEvent);
        }
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleSaveShortcut);
    }
    return () => document.removeEventListener("keydown", handleSaveShortcut);
  }, [isOpen, createMutation.isPending, updateMutation.isPending, handleSubmit]);

  // Auto-focus first input when modal opens
  useEffect(() => {
    if (isOpen && !isLoadingConfig) {
      setTimeout(() => {
        firstInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, isLoadingConfig]);

  // Add keyword group helper
  const addKeywordGroup = () => {
    if (!newKeywordGroup.trim()) return;

    const keywords = newKeywordGroup
      .split(",")
      .map((k) => k.trim())
      .filter((k) => k.length > 0);

    if (keywords.length === 0) return;

    setFormData({
      ...formData,
      classification: {
        ...formData.classification,
        keywordGroups: [...formData.classification.keywordGroups, keywords],
      },
    });
    setNewKeywordGroup("");
  };

  const removeKeywordGroup = (index: number) => {
    setFormData({
      ...formData,
      classification: {
        ...formData.classification,
        keywordGroups: formData.classification.keywordGroups.filter(
          (_, i) => i !== index
        ),
      },
    });
  };

  // Array field helpers
  const updateArrayField = (
    section: "contentExtraction",
    field: "imageBlacklist" | "imageWhitelist" | "attachmentPriority",
    value: string
  ) => {
    setArrayFieldRaw((prev) => ({ ...prev, [field]: value }));
    const items = value
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    setFormData({
      ...formData,
      [section]: {
        ...formData[section],
        [field]: items,
      },
    });
  };

  // Focus trap implementation
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleFocusTrap = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || !modalRef.current) return;

      const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        // Shift+Tab: if on first element, go to last
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab: if on last element, go to first
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener("keydown", handleFocusTrap);
    return () => document.removeEventListener("keydown", handleFocusTrap);
  }, [isOpen]);

  if (!isOpen) return null;

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto">
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="processing-config-modal-title"
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 my-8"
      >
        <div className="px-6 py-4 border-b border-neutral-200">
          <h2
            id="processing-config-modal-title"
            className="text-lg font-medium text-neutral-900"
          >
            {configId
              ? "Edytuj konfigurację przetwarzania"
              : "Dodaj nową konfigurację"}
          </h2>
        </div>

        <ErrorBoundary
          fallback={
            <div className="p-6 text-center">
              <p className="text-red-600 mb-4">
                Wystapil blad podczas ladowania formularza.
              </p>
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-neutral-700 bg-neutral-100 rounded-md hover:bg-neutral-200"
              >
                Zamknij
              </button>
            </div>
          }
        >
        {/* Tabs */}
        <div className="border-b border-neutral-200">
          <nav
            role="tablist"
            aria-label="Sekcje konfiguracji"
            className="flex -mb-px px-6"
          >
            {[
              { id: "basic", label: "Podstawowe" },
              { id: "extraction", label: "Ekstrakcja treści" },
              { id: "classification", label: "Klasyfikacja" },
              { id: "data", label: "Dane LLM" },
              ...(configId ? [{ id: "history", label: "Historia zmian" }] : []),
            ].map((tab) => (
              <button
                key={tab.id}
                role="tab"
                id={`tab-${tab.id}`}
                aria-selected={activeTab === tab.id}
                aria-controls={`panel-${tab.id}`}
                onClick={() => setActiveTab(tab.id as any)}
                className={`px-4 py-2 text-sm font-medium border-b-2 ${
                  activeTab === tab.id
                    ? "border-primary-600 text-primary-600"
                    : "border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
          {globalError && (
            <div
              className="p-3 bg-red-50 border border-red-200 rounded-md"
              role="alert"
            >
              <p className="text-sm text-red-700">{globalError}</p>
            </div>
          )}

          {isLoadingConfig ? (
            <div className="text-center py-8" role="status" aria-live="polite">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-sm text-neutral-600">Ładowanie...</p>
            </div>
          ) : (
            <>
              {/* Basic Tab */}
              {activeTab === "basic" && (
                <div
                  role="tabpanel"
                  id="panel-basic"
                  aria-labelledby="tab-basic"
                  className="space-y-4"
                >
                  <div>
                    <label
                      htmlFor="config-name"
                      className="block text-sm font-medium text-neutral-700 mb-1"
                    >
                      Nazwa <span className="text-red-600">*</span>
                    </label>
                    <input
                      ref={firstInputRef}
                      id="config-name"
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      aria-invalid={!!errors.name}
                      aria-describedby={errors.name ? "name-error" : undefined}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 ${
                        errors.name ? "border-red-500" : "border-neutral-300"
                      }`}
                      placeholder="np. KFS, BUR, Default"
                      maxLength={100}
                      required
                    />
                    {errors.name && (
                      <span id="name-error" role="alert" className="mt-1 text-xs text-red-600 block">
                        {errors.name}
                      </span>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Opis
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({ ...formData, description: e.target.value })
                      }
                      rows={3}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                      placeholder="Opis konfiguracji..."
                      maxLength={2000}
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isDefault"
                      checked={formData.isDefault}
                      onChange={(e) =>
                        setFormData({ ...formData, isDefault: e.target.checked })
                      }
                      className="h-4 w-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
                    />
                    <label
                      htmlFor="isDefault"
                      className="ml-2 text-sm text-neutral-700"
                    >
                      Ustaw jako domyślną konfigurację
                    </label>
                  </div>
                </div>
              )}

              {/* Extraction Tab */}
              {activeTab === "extraction" && (
                <div
                  role="tabpanel"
                  id="panel-extraction"
                  aria-labelledby="tab-extraction"
                  className="space-y-4"
                >
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="extractAttachments"
                      checked={formData.contentExtraction.extractAttachments}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          contentExtraction: {
                            ...formData.contentExtraction,
                            extractAttachments: e.target.checked,
                          },
                        })
                      }
                      className="h-4 w-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
                    />
                    <label
                      htmlFor="extractAttachments"
                      className="ml-2 text-sm text-neutral-700"
                    >
                      Wyciągaj tekst z załączników PDF
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="extractImages"
                      checked={formData.contentExtraction.extractImages}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          contentExtraction: {
                            ...formData.contentExtraction,
                            extractImages: e.target.checked,
                          },
                        })
                      }
                      className="h-4 w-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
                    />
                    <label
                      htmlFor="extractImages"
                      className="ml-2 text-sm text-neutral-700"
                    >
                      Wykonuj OCR na obrazkach
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Maksymalna liczba załączników
                    </label>
                    <input
                      type="number"
                      value={formData.contentExtraction.maxAttachments}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          contentExtraction: {
                            ...formData.contentExtraction,
                            maxAttachments: Math.min(
                              10,
                              Math.max(0, parseInt(e.target.value) || 0)
                            ),
                          },
                        })
                      }
                      min={0}
                      max={10}
                      className="w-24 px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Blacklista obrazków
                    </label>
                    <input
                      type="text"
                      value={arrayFieldRaw.imageBlacklist}
                      onChange={(e) =>
                        updateArrayField(
                          "contentExtraction",
                          "imageBlacklist",
                          e.target.value
                        )
                      }
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                      placeholder="logo, banner, stopka (rozdziel przecinkami)"
                    />
                    <p className="mt-1 text-xs text-neutral-500">
                      Pomiń obrazki zawierające te słowa w nazwie
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Priorytet załączników
                    </label>
                    <input
                      type="text"
                      value={arrayFieldRaw.attachmentPriority}
                      onChange={(e) =>
                        updateArrayField(
                          "contentExtraction",
                          "attachmentPriority",
                          e.target.value
                        )
                      }
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                      placeholder="komunikat, nabór, regulamin (rozdziel przecinkami)"
                    />
                    <p className="mt-1 text-xs text-neutral-500">
                      Załączniki z tymi słowami będą przetwarzane pierwsze
                    </p>
                  </div>
                </div>
              )}

              {/* Classification Tab */}
              {activeTab === "classification" && (
                <div
                  role="tabpanel"
                  id="panel-classification"
                  aria-labelledby="tab-classification"
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Grupy słów kluczowych
                    </label>
                    <p className="text-xs text-neutral-500 mb-2">
                      Artykuł musi zawierać co najmniej jedno słowo z KAŻDEJ
                      grupy (logika AND)
                    </p>

                    {/* Existing groups */}
                    <div className="space-y-2 mb-4">
                      {formData.classification.keywordGroups.map(
                        (group, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-2 p-2 bg-neutral-50 rounded-md"
                          >
                            <span className="text-xs text-neutral-500">
                              Grupa {index + 1}:
                            </span>
                            <span className="flex-1 text-sm">
                              {group.join(", ")}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeKeywordGroup(index)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              Usuń
                            </button>
                          </div>
                        )
                      )}
                    </div>

                    {/* Add new group */}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newKeywordGroup}
                        onChange={(e) => setNewKeywordGroup(e.target.value)}
                        className="flex-1 px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                        placeholder="nabór, konkurs, dofinansowanie (rozdziel przecinkami)"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addKeywordGroup();
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={addKeywordGroup}
                        className="px-4 py-2 text-sm font-medium text-primary-600 border border-primary-600 rounded-md hover:bg-primary-50"
                      >
                        Dodaj grupę
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="useInflections"
                      checked={formData.classification.useInflections}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          classification: {
                            ...formData.classification,
                            useInflections: e.target.checked,
                          },
                        })
                      }
                      className="h-4 w-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
                    />
                    <label
                      htmlFor="useInflections"
                      className="ml-2 text-sm text-neutral-700"
                    >
                      Automatycznie generuj odmiany słów
                    </label>
                  </div>
                </div>
              )}

              {/* Data Extraction Tab */}
              {activeTab === "data" && (
                <div
                  role="tabpanel"
                  id="panel-data"
                  aria-labelledby="tab-data"
                  className="space-y-4"
                >
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="preferHtml"
                      checked={formData.dataExtraction.preferHtml}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          dataExtraction: {
                            ...formData.dataExtraction,
                            preferHtml: e.target.checked,
                          },
                        })
                      }
                      className="h-4 w-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
                    />
                    <label
                      htmlFor="preferHtml"
                      className="ml-2 text-sm text-neutral-700"
                    >
                      Używaj HTML zamiast czystego tekstu jako wejścia LLM
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Prompt ekstrakcji dat
                    </label>
                    <textarea
                      value={formData.dataExtraction.prompts.dateExtraction}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          dataExtraction: {
                            ...formData.dataExtraction,
                            prompts: {
                              ...formData.dataExtraction.prompts,
                              dateExtraction: e.target.value,
                            },
                          },
                        })
                      }
                      rows={4}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 font-mono text-sm"
                      placeholder="Wyciągnij daty składania wniosków z podanego tekstu..."
                      maxLength={2000}
                    />
                    <p className="mt-1 text-xs text-neutral-500">
                      Prompt używany przez LLM do ekstrakcji dat
                    </p>
                  </div>

                  <div>
                    <label
                      htmlFor="output-schema"
                      className="block text-sm font-medium text-neutral-700 mb-1"
                    >
                      Schema outputu (JSON)
                    </label>
                    <textarea
                      id="output-schema"
                      value={JSON.stringify(
                        formData.dataExtraction.prompts.outputSchema,
                        null,
                        2
                      )}
                      onChange={(e) => {
                        try {
                          const parsed = JSON.parse(e.target.value);
                          setFormData({
                            ...formData,
                            dataExtraction: {
                              ...formData.dataExtraction,
                              prompts: {
                                ...formData.dataExtraction.prompts,
                                outputSchema: parsed,
                              },
                            },
                          });
                          setErrors({ ...errors, outputSchema: "" });
                        } catch {
                          setErrors({
                            ...errors,
                            outputSchema: "Nieprawidłowy format JSON",
                          });
                        }
                      }}
                      rows={8}
                      aria-invalid={!!errors.outputSchema}
                      aria-describedby={errors.outputSchema ? "output-schema-error" : undefined}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 font-mono text-sm ${
                        errors.outputSchema
                          ? "border-red-500"
                          : "border-neutral-300"
                      }`}
                      placeholder='{ "type": "object", "properties": { ... } }'
                    />
                    {errors.outputSchema && (
                      <span id="output-schema-error" role="alert" className="mt-1 text-xs text-red-600 block">
                        {errors.outputSchema}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* History Tab - Audit Log */}
              {activeTab === "history" && configId && (
                <div
                  role="tabpanel"
                  id="panel-history"
                  aria-labelledby="tab-history"
                  className="space-y-4"
                >
                  <AuditLogTimeline
                    entries={auditData?.data || []}
                    isLoading={isLoadingAudit}
                    error={
                      auditError
                        ? "Nie udało się załadować historii zmian"
                        : null
                    }
                    onRetry={() => refetchAudit()}
                    emptyMessage="Brak historii zmian"
                    emptyDescription="Ta konfiguracja nie była jeszcze modyfikowana."
                    fieldLabels={{
                      ...DEFAULT_FIELD_LABELS,
                      contentExtraction: "Ekstrakcja treści",
                      classification: "Klasyfikacja",
                      dataExtraction: "Ekstrakcja danych",
                      isDefault: "Domyślna",
                    }}
                    hasMore={auditData?.pagination?.hasMore}
                  />
                </div>
              )}
            </>
          )}
        </form>

        <div className="px-6 py-4 border-t border-neutral-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-neutral-700 bg-neutral-100 rounded-md hover:bg-neutral-200 disabled:opacity-50"
          >
            Anuluj
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || isLoadingConfig}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading && (
              <div role="status" aria-live="polite">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span className="sr-only">Zapisywanie...</span>
              </div>
            )}
            {isLoading
              ? "Zapisywanie..."
              : configId
              ? "Zaktualizuj"
              : "Dodaj konfigurację"}
          </button>
        </div>
        </ErrorBoundary>
      </div>
    </div>
  );
};

export default EditProcessingConfigModal;
